/** SUNTRONIX ONE — Work → Projects & Tasks. */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderKanban, PlusCircle, X, Sparkles, ListChecks, LayoutGrid,
  CalendarDays, User, AlertTriangle, Trash2, Link2 } from "lucide-react";

const din = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const PRIO_TINT = { LOW: "bg-slate-100 text-slate-600", MEDIUM: "bg-blue-100 text-blue-800",
  HIGH: "bg-amber-100 text-amber-800", URGENT: "bg-rose-100 text-rose-800" };
const ST_TINT = { PLANNING: "bg-slate-100 text-slate-600", ACTIVE: "bg-teal-100 text-teal-800",
  ON_HOLD: "bg-amber-100 text-amber-800", COMPLETED: "bg-emerald-100 text-emerald-800", CANCELLED: "bg-rose-100 text-rose-700" };
const TASK_COLS = [["TODO", "To do"], ["IN_PROGRESS", "In progress"], ["BLOCKED", "Blocked"], ["DONE", "Done"]];

export default function ProjectsWorkspace() {
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(sp.get("open") || null);
  const [create, setCreate] = useState(sp.get("new") === "1");

  const load = useCallback(() => {
    api.get("/projects/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-[1150px] space-y-4 pb-16" data-testid="projects-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><FolderKanban className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Projects &amp; Tasks</div>
          <div className="text-[11px] text-slate-500">Deployments · rollouts · audits · recovery · onboarding — tasks flow into ONE Today</div>
        </div>
        {meta && (
          <button onClick={() => setCreate(true)} data-testid="project-new"
            className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">
            <PlusCircle className="h-3.5 w-3.5" /> New project
          </button>
        )}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-list">
        {projects.map((p) => (
          <button key={p.id} onClick={() => setOpenId(p.id)} data-testid={`project-${p.id}`}
            className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-teal-400">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-slate-400">{p.project_number}</span>
              <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${ST_TINT[p.status]}`}>{p.status}</span>
            </div>
            <div className="mt-1 text-[14px] font-semibold text-slate-900">{p.name}</div>
            <div className="text-[11px] text-slate-500">{p.customer_name || "—"} · {p.project_type?.replace(/_/g, " ")}</div>
            <div className="mt-2">
              <div className="h-1.5 w-full rounded-full bg-slate-100">
                <div className="h-1.5 rounded-full bg-teal-600" style={{ width: `${p.progress?.pct || 0}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-slate-500">
                <span>{p.progress?.done}/{p.progress?.total} done</span>
                {p.progress?.overdue > 0 && <span className="text-rose-600">{p.progress.overdue} overdue</span>}
              </div>
            </div>
          </button>
        ))}
        {!projects.length && <div className="col-span-full rounded-xl border border-dashed border-slate-200 p-10 text-center text-[12px] text-slate-400">No projects yet. Create one from a template.</div>}
      </div>

      {openId && <ProjectDetail pid={openId} meta={meta} onClose={() => setOpenId(null)} onChange={load} />}
      {create && <CreateDialog meta={meta} onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}
    </div>
  );
}

function ProjectDetail({ pid, meta, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("list");
  const [filter, setFilter] = useState("all");
  const [ai, setAi] = useState({ loading: false, text: "" });
  const [nt, setNt] = useState({ title: "", assigned_to: "", due_date: "", priority: "MEDIUM" });

  const load = useCallback(() => { api.get(`/projects/${pid}`).then(({ data }) => setData(data)).catch(() => {}); }, [pid]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (tid, status) => {
    try { await api.patch(`/projects/tasks/${tid}`, { status }); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addTask = async () => {
    if (!nt.title.trim()) return;
    await api.post(`/projects/${pid}/tasks`, { ...nt, due_date: nt.due_date || null });
    setNt({ title: "", assigned_to: "", due_date: "", priority: "MEDIUM" }); load(); onChange?.();
  };
  const delTask = async (tid) => { await api.delete(`/projects/tasks/${tid}`); load(); onChange?.(); };
  const runAi = async (mode) => {
    setAi({ loading: true, text: "" });
    try { const { data } = await api.post(`/projects/${pid}/ai`, { mode });
      setAi({ loading: false, text: data.answer || (data.items || []).map((i) => `• ${i.title}`).join("\n") || "No results." });
    } catch { setAi({ loading: false, text: "AI failed" }); }
  };

  if (!data) return null;
  const p = data.project;
  const today = new Date().toISOString().slice(0, 10);
  let tasks = data.tasks;
  if (filter === "mine") tasks = tasks.filter((t) => t.assigned_to === meta?.me);
  else if (filter === "overdue") tasks = tasks.filter((t) => t.status !== "DONE" && (t.due_date || "9999") < today);
  else if (filter === "today") tasks = tasks.filter((t) => t.due_date === today && t.status !== "DONE");

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/50 p-0 sm:p-4 sm:pt-6" onClick={onClose}>
      <div className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[93vh] sm:rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="project-detail">
        <div className="flex items-center gap-2 bg-teal-900 px-4 py-3 text-white">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] text-teal-200">{p.project_number} · {p.project_type?.replace(/_/g, " ")}</div>
            <div className="truncate text-[14px] font-semibold">{p.name}</div>
          </div>
          <span className="rounded bg-white/15 px-2 py-0.5 text-[10px] font-semibold">{p.status}</span>
          <button onClick={onClose} data-testid="project-detail-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="border-b border-slate-100 px-4 py-2 text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5">
          {p.customer_name && <span>👤 {p.customer_name}</span>}
          {p.owner_email && <span>Owner: {p.owner_email}</span>}
          {p.target_date && <span>🎯 {p.target_date}</span>}
          {(p.locations || []).length > 0 && <span>📍 {p.locations.join(", ")}</span>}
          {p.linked_ref && <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> {p.linked_type}: {p.linked_ref}</span>}
        </div>

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
          <div className="flex rounded-md border border-slate-200 p-0.5 text-[11px]">
            <button onClick={() => setView("list")} data-testid="view-list" className={`inline-flex items-center gap-1 rounded px-2 py-1 ${view === "list" ? "bg-teal-800 text-white" : "text-slate-600"}`}><ListChecks className="h-3 w-3" /> List</button>
            <button onClick={() => setView("board")} data-testid="view-board" className={`inline-flex items-center gap-1 rounded px-2 py-1 ${view === "board" ? "bg-teal-800 text-white" : "text-slate-600"}`}><LayoutGrid className="h-3 w-3" /> Board</button>
            <button onClick={() => setView("timeline")} data-testid="view-timeline" className={`inline-flex items-center gap-1 rounded px-2 py-1 ${view === "timeline" ? "bg-teal-800 text-white" : "text-slate-600"}`}><CalendarDays className="h-3 w-3" /> Timeline</button>
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-[11px]" data-testid="task-filter">
            <option value="all">All tasks</option><option value="mine">My tasks</option><option value="overdue">Overdue</option><option value="today">Today</option>
          </select>
          <div className="ml-auto flex gap-1">
            {[["summarise", "Summarise"], ["overdue", "Overdue"], ["draft_plan", "Draft plan"], ["blockers", "Blockers"]].map(([m, l]) => (
              <button key={m} onClick={() => runAi(m)} data-testid={`project-ai-${m}`} className="inline-flex items-center gap-1 rounded-md border border-violet-200 px-2 py-1 text-[10px] font-semibold text-violet-700"><Sparkles className="h-3 w-3" /> {l}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {ai.text || ai.loading ? (
            <div className="mb-3 whitespace-pre-wrap rounded-md border border-violet-200 bg-violet-50 p-2.5 text-[11px] text-slate-700" data-testid="project-ai-result">{ai.loading ? "Thinking…" : ai.text}</div>
          ) : null}

          {view === "board" ? (
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="board-view">
              {TASK_COLS.map(([st, label]) => (
                <div key={st} className="rounded-lg bg-slate-50 p-2">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase text-slate-500">{label} ({tasks.filter((t) => t.status === st).length})</div>
                  {tasks.filter((t) => t.status === st).map((t) => (
                    <div key={t.id} className="mb-1.5 rounded-md border border-slate-200 bg-white p-2 text-[11px]" data-testid={`task-card-${t.id}`}>
                      <div className="font-medium text-slate-800">{t.title}</div>
                      <div className="mt-1 flex items-center gap-1">
                        <span className={`rounded px-1 py-0.5 text-[8px] font-semibold ${PRIO_TINT[t.priority]}`}>{t.priority}</span>
                        {t.due_date && <span className="text-[9px] text-slate-400">{t.due_date}</span>}
                      </div>
                      <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="mt-1 w-full rounded border border-slate-200 text-[9px]">
                        {TASK_COLS.map(([s]) => <option key={s} value={s}>{s}</option>)}</select>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="list-view">
              {tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px]" data-testid={`task-row-${t.id}`}>
                  <input type="checkbox" checked={t.status === "DONE"} onChange={(e) => setStatus(t.id, e.target.checked ? "DONE" : "TODO")} data-testid={`task-done-${t.id}`} />
                  <span className={`flex-1 ${t.status === "DONE" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</span>
                  {t.assigned_to && <span className="hidden sm:inline text-[9px] text-slate-400"><User className="mr-0.5 inline h-3 w-3" />{t.assigned_to.split("@")[0]}</span>}
                  {t.due_date && <span className={`text-[9px] ${t.status !== "DONE" && t.due_date < today ? "font-semibold text-rose-600" : "text-slate-400"}`}>{t.due_date}</span>}
                  <span className={`rounded px-1 py-0.5 text-[8px] font-semibold ${PRIO_TINT[t.priority]}`}>{t.priority}</span>
                  <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="rounded border border-slate-200 text-[9px]">
                    {TASK_COLS.map(([s]) => <option key={s} value={s}>{s}</option>)}</select>
                  <button onClick={() => delTask(t.id)} className="text-rose-400"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              {!tasks.length && <div className="py-6 text-center text-[11px] text-slate-400">No tasks in this view.</div>}
              {/* quick add */}
              <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-5">
                <input value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} placeholder="New task" className="col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" data-testid="new-task-title" />
                <input value={nt.assigned_to} onChange={(e) => setNt({ ...nt, assigned_to: e.target.value })} placeholder="assignee@" className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" />
                <input type="date" value={nt.due_date} onChange={(e) => setNt({ ...nt, due_date: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" />
                <button onClick={addTask} data-testid="add-task" className="rounded-md bg-teal-800 px-2 py-1.5 text-[11px] font-semibold text-white">+ Task</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateDialog({ meta, onClose, onSaved }) {
  const [tab, setTab] = useState("template");
  const [tpl, setTpl] = useState(meta?.templates?.[0]?.key || "");
  const [f, setF] = useState({ name: "", customer_name: "", start_date: "", project_type: "DEPLOYMENT", target_date: "", scope: "" });

  const createTpl = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    await api.post(`/projects/from-template?template=${tpl}&name=${encodeURIComponent(f.name)}${f.customer_name ? `&customer_name=${encodeURIComponent(f.customer_name)}` : ""}${f.start_date ? `&start_date=${f.start_date}` : ""}`);
    toast.success("Project created from template"); onSaved();
  };
  const createBlank = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    await api.post("/projects", { ...f });
    toast.success("Project created"); onSaved();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-12 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="project-create-dialog">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[13px] font-semibold">New project</div>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button>
        </div>
        <div className="mb-3 flex gap-1 text-[11px]">
          <button onClick={() => setTab("template")} data-testid="tab-template" className={`rounded px-3 py-1.5 font-semibold ${tab === "template" ? "bg-teal-800 text-white" : "bg-slate-100 text-slate-600"}`}>From template</button>
          <button onClick={() => setTab("blank")} data-testid="tab-blank" className={`rounded px-3 py-1.5 font-semibold ${tab === "blank" ? "bg-teal-800 text-white" : "bg-slate-100 text-slate-600"}`}>Blank</button>
        </div>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Project name" className={din} data-testid="project-name" />
        <input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} placeholder="Customer" className={din} data-testid="project-customer" />
        {tab === "template" ? (
          <>
            <label className="mt-2 block text-[10px] text-slate-500">Template
              <select value={tpl} onChange={(e) => setTpl(e.target.value)} className={din} data-testid="project-template">
                {(meta?.templates || []).map((t) => <option key={t.key} value={t.key}>{t.label} ({t.task_count} tasks)</option>)}</select></label>
            <label className="mt-2 block text-[10px] text-slate-500">Start date
              <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={din} /></label>
            <button onClick={createTpl} data-testid="create-from-template" className="mt-3 w-full rounded-md bg-teal-800 py-2 text-[12px] font-semibold text-white">Create with tasks</button>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] text-slate-500">Type
                <select value={f.project_type} onChange={(e) => setF({ ...f, project_type: e.target.value })} className={din}>
                  {(meta?.project_types || []).map((t) => <option key={t}>{t}</option>)}</select></label>
              <label className="text-[10px] text-slate-500">Target date
                <input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} className={din} /></label>
            </div>
            <textarea value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} rows={2} placeholder="Scope" className={din} />
            <button onClick={createBlank} data-testid="create-blank" className="mt-3 w-full rounded-md bg-teal-800 py-2 text-[12px] font-semibold text-white">Create project</button>
          </>
        )}
      </div>
    </div>
  );
}
